import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Header } from "@/components/brand/Logo";
import { flow } from "@/lib/flow";
import { track } from "@/lib/tracking";

const HERO = "https://static.prod-images.emergentagent.com/jobs/074de523-0849-4300-9722-be6dd5fc451a/images/f1335cc4889e7fbdc71eb19584e8dea31d0968cf90ec39a57f5c33045e712075.jpeg";

const STEPS = [
  ["01", "Ascoltiamo la tua pelle", "Otto domande semplici, una alla volta."],
  ["02", "Ti raccontiamo cosa abbiamo capito", "Il tuo Skin Profile, prima di qualsiasi prodotto."],
  ["03", "Costruiamo il tuo rituale", "Giorno e notte, solo con ciò che è compatibile con te."],
];

const fade = (d = 0) => ({ initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, delay: d } });

export default function Landing() {
  const navigate = useNavigate();
  const inProgress = Object.keys(flow.answers()).length > 0 && !flow.result();

  useEffect(() => { track("advisor_viewed", { path: "/" }); }, []);

  const start = () => {
    if (!inProgress) { flow.restart(); }
    navigate("/test");
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="landing-page">
      <Header />
      <main className="grid lg:grid-cols-12 gap-10 px-6 sm:px-10 pt-4 pb-20 max-w-7xl mx-auto">
        <section className="lg:col-span-6 lg:pt-16 order-2 lg:order-1">
          <motion.p {...fade(0.1)} className="fs-eyebrow">Consulenza digitale FineSkin</motion.p>
          <motion.h1 {...fade(0.2)} className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight mt-6">
            La tua pelle merita <em className="text-[#8C6F3E]">ascolto</em>, non un tentativo.
          </motion.h1>
          <motion.p {...fade(0.3)} className="mt-6 text-base text-[#595652] leading-relaxed max-w-md font-light">
            Raccontaci come si sente la tua pelle. La nostra Beauty Advisor capisce le sue esigenze e ti propone un
            rituale giorno e notte pensato solo per te.
          </motion.p>
          <motion.div {...fade(0.4)} className="mt-10 flex flex-col sm:flex-row sm:items-center gap-5">
            <button data-testid="start-beauty-test-btn" className="fs-btn" onClick={start}>
              {inProgress ? "Riprendi il mio test" : "Crea la mia routine"} <ArrowRight size={16} strokeWidth={1.5} />
            </button>
            <span data-testid="landing-microcopy" className="text-xs text-[#8C8275] tracking-wide">~2 minuti · Nessuna registrazione</span>
          </motion.div>
          {inProgress && (
            <button data-testid="landing-restart-btn" className="fs-btn-ghost mt-6" onClick={() => { flow.restart(); navigate("/test"); }}>
              Ricomincia da capo
            </button>
          )}
          <motion.ol {...fade(0.55)} className="mt-16 space-y-6 border-t border-[#E6DFD5] pt-8 max-w-md">
            {STEPS.map(([n, t, d]) => (
              <li key={n} className="flex gap-5">
                <span className="font-serif italic text-[#C5A059] text-lg w-6">{n}</span>
                <div>
                  <p className="font-serif text-lg">{t}</p>
                  <p className="text-sm text-[#8C8275] mt-1">{d}</p>
                </div>
              </li>
            ))}
          </motion.ol>
        </section>
        <motion.figure
          initial={{ opacity: 0, scale: 1.02 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.1 }}
          className="lg:col-span-6 order-1 lg:order-2 relative"
        >
          <div className="aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] overflow-hidden rounded-t-[200px] bg-[#F2ECE4]">
            <img src={HERO} alt="Pelle luminosa" className="w-full h-full object-cover object-[50%_25%]" />
          </div>
          <figcaption className="absolute -bottom-5 left-6 bg-[#FAF8F5] px-5 py-3 border border-[#E6DFD5]">
            <span className="fs-eyebrow">Servizio prima del prodotto</span>
          </figcaption>
        </motion.figure>
      </main>
    </div>
  );
}
